/* eslint-disable no-void */
import * as vp from 'vopay';
import { FunctionComponent, useState, useEffect } from 'react';
import { FormikErrors, FormikTouched } from 'formik';

import { useNavMenu } from '@/providers/NavMenu.provider';
import { useClientData } from '@/providers/ClientData.provider';
import { useAccountInfo } from '@/providers/AccountInfo.provider';
import { usePermissions } from '@/providers/Permissions.provider';
import { BankAccountTransferHandler, BankAccountTransferRequest } from './types';
import AccountCreationContainer from '../AccountCreation/AccountCreationContainer';
import { PendingDataProvider } from '@/providers/PendingData.provider';
import BankAccountTransferReview from './BankAccountTransferReview';
import { BankAccountTransferRows } from './buildBankAccountTransferRows.helper';
import BankAccountTransferForm from './BankAccountTransferForm';
import useFetchClientBankAccounts from '@/hooks/useApi/useFetchClientBankAccounts';
import { useUserSettingsProvider } from '@/providers/UserSettings.provider';

const { Box, Title } = vp;

type FC = FunctionComponent;

const BankAccountTransferContainer: FC = () => {
  const { userSettings } = useUserSettingsProvider();
  const { getModuleTitle } = useNavMenu();
  const { setNewToken } = useAccountInfo();
  const { permissions } = usePermissions();
  const [review, setReview] = useState(false);
  const [createAccountActive, setCreateAccountActive] = useState(false);
  const [doubleNewAccount, setDoubleNewAccount] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [currentStepSend] = useState(false);
  const [submitTransaction, setSubmitTransaction] = useState(false);
  const [formErrors, setFormErrors] = useState<FormikErrors<BankAccountTransferRequest>>({});
  const [formTouched, setFormTouched] = useState<FormikTouched<BankAccountTransferRequest>>({});
  const [accountRowsForm, setAccountRowsForm] = useState<BankAccountTransferRows[]>([]);
  const [submitSubForm, setSubmitForm] = useState<() => Promise<void>>(() => Promise.resolve());
  const [formSubmittedOnce, setFormSubmittedOnce] = useState(false);
  const [bankAccountsResponseFirst, fetchClientBankAccountsFirst, resetBankAccountFirst] = useFetchClientBankAccounts(
    {},
  );
  const [bankAccountsResponseSecond, fetchClientBankAccountsSecond] = useFetchClientBankAccounts({});
  const {
    response: { Country },
  } = useClientData();
  const [formData, setFormData] = useState<BankAccountTransferRequest>({
    Currency: Country === 'US' ? 'USD' : 'CAD',
    From: '',
    To: '',
    Amount: '0.0',
  });

  const [fromBankInfo, setFromBankInfo] = useState<vp.ClientBankAccountsLists>();
  const [toBankInfo, setToBankInfo] = useState<vp.ClientBankAccountsLists>();
  const [shouldUpdateFundAccountRequest, setShouldUpdateBankAccountTransferRequest] = useState(false);

  const handleToken = (token: string) => {
    setNewToken(token !== '' ? token : 'new');
  };

  useEffect(() => {
    if (bankAccountsResponseFirst.Success) {
      const bankAccount = Object.values(bankAccountsResponseFirst.BankAccounts).pop();

      if (formData.From === 'new') {
        setFromBankInfo(bankAccount);
      } else {
        setToBankInfo(bankAccount);
      }
    }
  }, [bankAccountsResponseFirst]);

  useEffect(() => {
    if (bankAccountsResponseSecond.Success) {
      const bankAccount = Object.values(bankAccountsResponseSecond.BankAccounts).pop();
      setToBankInfo(bankAccount);
    }
  }, [bankAccountsResponseSecond]);

  const handlers: BankAccountTransferHandler = {
    onReview: () => {},
    onChange: (_values, errors, touched, submitForm, accountRows) => {
      setFormErrors(errors);
      setFormTouched(touched);
      resetBankAccountFirst();
      setFormData(_values);

      if (_values.From === 'new' && _values.To === 'new') {
        setCreateAccountActive(true);
        setDoubleNewAccount(true);
      } else if (_values.From === 'new' || _values.To === 'new') {
        setCreateAccountActive(true);
        setDoubleNewAccount(false);
      } else {
        setCreateAccountActive(false);
        setDoubleNewAccount(false);
      }

      setAccountRowsForm(accountRows);
      setSubmitForm(() => submitForm);
      if (currentStep === 0 && Object.values(formErrors).length !== 0 && formSubmittedOnce) {
        void submitForm();
      }
    },
    onBack: () => {
      setReview(false);
      setShouldUpdateBankAccountTransferRequest(true);
    },
  };

  useEffect(() => {
    if (shouldUpdateFundAccountRequest) {
      setFormData({
        Currency: formData.Currency,
        From: formData.From,
        To: formData.To,
        Amount: formData.Amount,
      });
    }
  }, [review]);

  const onStepChange = (IdStep: number) => {
    setCurrentStep(IdStep);
  };
  const onStepReject = (IdStep: number) => {
    if (IdStep === 0) {
      void submitSubForm();
    }
  };
  const onMainFormSubmit = () => {
    if (!permissions.CreateBankAccountTransfer) return;
    if (Object.values(formErrors).length === 0) {
      setSubmitTransaction(true);
    } else {
      void submitSubForm();
      setFormSubmittedOnce(true);
    }
  };
  const onTeneativeSubmit = () => {
    if (!permissions.CreateBankAccountTransfer) return;
    if (Object.values(formErrors).length === 0 && createAccountActive === false) {
      setSubmitTransaction(true);
    } else {
      void submitSubForm();
      setFormSubmittedOnce(true);
    }
  };

  const onCompleteToken = (token: string, paymentMethodToken: string) => {
    if (paymentMethodToken === 'bank') {
      fetchClientBankAccountsFirst({
        ClientControlled: true,
        Token: token,
        Timezone: userSettings.timezone,
      });
    }
  };

  const onCompleteTokenSecondBank = (token: string, paymentMethodToken: string) => {
    if (paymentMethodToken === 'bank') {
      fetchClientBankAccountsSecond({
        ClientControlled: true,
        Token: token,
        Timezone: userSettings.timezone,
      });
    }
  };

  useEffect(() => {
    if (submitTransaction) {
      setTimeout(() => {
        setSubmitTransaction(false);
      }, 500);
    }
  }, [submitTransaction]);

  return (
    <Box mb="md" jc="sb" flx="col" gap="lg">
      <Box mb="md" jc="sb">
        <Title title={getModuleTitle()} />
      </Box>
      <PendingDataProvider>
        <AccountCreationContainer
          extraSteps={[
            {
              label: '',
              content: (
                <BankAccountTransferForm
                  handleToken={handleToken}
                  handlers={handlers}
                  defaultValues={formData}
                  sourceBank={fromBankInfo}
                  onSourceBankChange={setFromBankInfo}
                  destBank={toBankInfo}
                  onDestBankChange={setToBankInfo}
                />
              ),
              showStep: () => true,
              hasError: () => Object.values(formErrors).length !== 0 && formSubmittedOnce,
              percentageComplete: () => getCompletionPercentage(formData, formTouched, formErrors, true),
            },
          ]}
          customReview={
            fromBankInfo && toBankInfo ? (
              <BankAccountTransferReview
                defaultValues={formData}
                submitTransaction={submitTransaction}
                sourceBank={fromBankInfo}
                destBank={toBankInfo}
              />
            ) : (
              <></>
            )
          }
          isClientControlled
          isActive={createAccountActive}
          isAllowPaylink={false}
          onStepChange={onStepChange}
          onStepReject={onStepReject}
          stepUser={currentStep}
          showSave
          onCompleteToken={onCompleteToken}
          onCompleteTokenSecondBank={onCompleteTokenSecondBank}
          stepUserChange={currentStepSend}
          isAllowMove={(Object.values(formErrors).length === 0 || createAccountActive) && !!permissions.CreateBankAccountTransfer}
          onComplete={onMainFormSubmit}
          onTeneativeSubmit={onTeneativeSubmit}
          isAllow2Banks={doubleNewAccount}
          defaultValues={{
            currency: Country === 'US' ? 'USD' : 'CAD',
            paymentMethod: 'bank',
            country: Country,
            account: formData.From,
          }}
        />
      </PendingDataProvider>
    </Box>
  );
  function getCompletionPercentage(
    values: BankAccountTransferRequest,
    touched: FormikTouched<BankAccountTransferRequest>,
    errors: FormikErrors<BankAccountTransferRequest>,
    atLeastCurrentStep = true,
  ) {
    const fields = accountRowsForm.filter((x) => x.require);
    if (fields.length === 0) return atLeastCurrentStep ? 100 : 0;
    const filled = fields.filter((field) => (touched[field.id] || values[field.id]) && !errors[field.id]);
    return (filled.length / fields.length) * 100;
  }
};

export default BankAccountTransferContainer;
